/**
 * Utilitaires pour la gestion des layouts React-Grid-Layout
 * dans les dashboards Pulse
 */

// Configuration par défaut React-Grid-Layout
export const DEFAULT_GRID_CONFIG = {
  breakpoints: { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 },
  cols: { lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 },
  rowHeight: 24,
  margin: [10, 10],
  containerPadding: [0, 0],
  compactType: 'vertical',
  preventCollision: false,
  useCSSTransforms: true,
  isDraggable: true,
  isResizable: true,
  autoSize: true,
};

// Tailles par défaut pour différents types de blocs
export const DEFAULT_BLOCK_SIZES = {
  number: { w: 3, h: 2 },
  chart: { w: 6, h: 6 },
  table: { w: 6, h: 6 },
  map: { w: 6, h: 6 },
  text: { w: 4, h: 3 },
  filter: { w: 4, h: 2 },
  heatmap: { w: 6, h: 4 },
  ranking: { w: 6, h: 4 },
};

export const getDefaultBlockSize = block => block.type === 'number' && block.visual?.display === 'gauge'
  ? { w: 3, h: 4 }
  : DEFAULT_BLOCK_SIZES[block.type] || DEFAULT_BLOCK_SIZES.chart;

// Tailles minimales pour chaque type de bloc
export const MIN_BLOCK_SIZES = {
  number: { minW: 2, minH: 2 },
  chart: { minW: 3, minH: 3 },
  table: { minW: 3, minH: 4 },
  map: { minW: 4, minH: 4 },
  text: { minW: 3, minH: 2 },
  filter: { minW: 3, minH: 2 },
  heatmap: { minW: 4, minH: 3 },
  ranking: { minW: 3, minH: 3 },
};

/**
 * Legacy dashboards mix saved IDs and positional IDs. Reserve every saved ID
 * before filling gaps, otherwise deleting a block can make two keys collide.
 * Normalize once in the store and persist these IDs on the next actual edit.
 */
export const ensureBlockIds = (blocks) => {
  if (!Array.isArray(blocks)) return [];
  const validId = value => typeof value === 'string' && value.trim()
    ? value
    : typeof value === 'number' && Number.isFinite(value) ? String(value) : null;
  const reserved = new Set(blocks.map(block => validId(block.id)).filter(Boolean));
  const used = new Set();
  return blocks.map((block, index) => {
    let id = validId(block.id);
    if (!id || used.has(id)) {
      const base = `block_${index}`;
      id = base;
      let suffix = 1;
      while (reserved.has(id) || used.has(id)) id = `${base}__${suffix++}`;
    }
    used.add(id);
    return block.id === id ? block : { ...block, id };
  });
};

/**
 * Génère un layout React-Grid-Layout à partir des blocs du dashboard
 * @param {array} blocks - Blocs du dashboard
 * @param {string} breakpoint - Breakpoint actuel (lg, md, sm, xs, xxs)
 * @returns {array} Layout pour React-Grid-Layout
 */
export const generateLayout = (blocks, breakpoint = 'lg') => {
  if (!Array.isArray(blocks)) return [];
  const cols = DEFAULT_GRID_CONFIG.cols[breakpoint] || DEFAULT_GRID_CONFIG.cols.lg;
  const positiveSize = (value, fallback) =>
    Number.isFinite(value) && value > 0 ? Math.max(1, Math.round(value)) : fallback;

  return ensureBlockIds(blocks).map((block, index) => {
    const layout = block.layout || {};
    const defaultSize = getDefaultBlockSize(block);
    const minSize = MIN_BLOCK_SIZES[block.type] || MIN_BLOCK_SIZES.chart;
    const w = Math.min(positiveSize(layout.w, defaultSize.w), cols);
    const h = positiveSize(layout.h, defaultSize.h);
    const x = Number.isFinite(layout.x) ? layout.x : (index * defaultSize.w) % cols;
    const y = Number.isFinite(layout.y)
      ? layout.y
      : Math.floor((index * defaultSize.w) / cols) * defaultSize.h;

    return {
      i: block.id,
      x: Math.max(0, Math.min(Math.round(x), cols - w)),
      y: Math.max(0, Math.round(y)),
      w,
      h,
      // Existing compact blocks (including templates) must not grow on the
      // first resize just because their size is below the type's default minimum.
      minW: Math.min(minSize.minW, w),
      minH: Math.min(minSize.minH, h),
      static: block.static || false,
    };
  });
};

/**
 * Valide un layout pour s'assurer qu'il respecte les contraintes
 * @param {array} layout - Layout à valider
 * @param {string} breakpoint - Breakpoint à valider
 * @returns {object} Résultat de validation avec erreurs éventuelles
 */
export const validateLayout = (layout, breakpoint = 'lg') => {
  const validation = {
    isValid: true,
    errors: [],
    warnings: [],
  };

  const maxCols = DEFAULT_GRID_CONFIG.cols[breakpoint];

  layout.forEach((item, index) => {
    // Vérifier les limites de colonnes
    if (item.x + item.w > maxCols) {
      validation.errors.push(
        `Bloc ${item.i}: dépasse la largeur maximale (${maxCols} colonnes)`
      );
      validation.isValid = false;
    }

    // Vérifier les valeurs négatives
    if (item.x < 0 || item.y < 0) {
      validation.errors.push(`Bloc ${item.i}: position négative non autorisée`);
      validation.isValid = false;
    }

    // Vérifier les tailles minimales
    if (item.w < (item.minW || 1) || item.h < (item.minH || 1)) {
      validation.errors.push(
        `Bloc ${item.i}: taille inférieure au minimum requis`
      );
      validation.isValid = false;
    }

    // Vérifier les doublons d'ID
    const duplicates = layout.filter((l) => l.i === item.i);
    if (duplicates.length > 1) {
      validation.errors.push(`ID dupliqué: ${item.i}`);
      validation.isValid = false;
    }
  });

  // Vérifier les collisions si preventCollision est activé
  if (DEFAULT_GRID_CONFIG.preventCollision) {
    const collisions = detectCollisions(layout);
    if (collisions.length > 0) {
      validation.warnings.push(`${collisions.length} collision(s) détectée(s)`);
    }
  }

  return validation;
};

/**
 * Détecte les collisions entre les blocs
 * @param {array} layout - Layout à analyser
 * @returns {array} Liste des collisions détectées
 */
const detectCollisions = (layout) => {
  const collisions = [];

  for (let i = 0; i < layout.length; i++) {
    for (let j = i + 1; j < layout.length; j++) {
      const a = layout[i];
      const b = layout[j];

      // Vérifier si les rectangles se chevauchent
      if (
        !(
          a.x + a.w <= b.x ||
          b.x + b.w <= a.x ||
          a.y + a.h <= b.y ||
          b.y + b.h <= a.y
        )
      ) {
        collisions.push({ block1: a.i, block2: b.i });
      }
    }
  }

  return collisions;
};

/**
 * Optimise automatiquement le placement des blocs
 * @param {array} blocks - Blocs à placer
 * @param {string} breakpoint - Breakpoint cible
 * @returns {array} Layout optimisé
 */
export const optimizeLayout = (blocks, breakpoint = 'lg') => {
  if (!Array.isArray(blocks)) return [];

  const maxCols = DEFAULT_GRID_CONFIG.cols[breakpoint];
  const optimizedLayout = [];
  let currentX = 0;
  let currentY = 0;
  let maxHeightInRow = 0;

  ensureBlockIds(blocks).forEach((block) => {
    const defaultSize =
      DEFAULT_BLOCK_SIZES[block.type] || DEFAULT_BLOCK_SIZES.chart;

    // Si le bloc ne rentre pas dans la rangée actuelle, passer à la suivante
    if (currentX + defaultSize.w > maxCols) {
      currentY += maxHeightInRow;
      currentX = 0;
      maxHeightInRow = 0;
    }

    optimizedLayout.push({
      i: block.id,
      x: currentX,
      y: currentY,
      w: defaultSize.w,
      h: defaultSize.h,
      ...MIN_BLOCK_SIZES[block.type],
    });

    currentX += defaultSize.w;
    maxHeightInRow = Math.max(maxHeightInRow, defaultSize.h);
  });

  return optimizedLayout;
};

/**
 * Exporte un layout pour sauvegarde
 * @param {array} layout - Layout à exporter
 * @returns {object} Layout exporté avec métadonnées
 */
export const exportLayout = (layout) => {
  return {
    version: '1.0',
    timestamp: Date.now(),
    config: DEFAULT_GRID_CONFIG,
    layout: layout.map((item) => ({
      id: item.i,
      x: item.x,
      y: item.y,
      w: item.w,
      h: item.h,
      static: item.static || false,
    })),
  };
};

/**
 * Importe un layout depuis une sauvegarde
 * @param {object} exportedLayout - Layout exporté
 * @returns {array} Layout importé
 */
export const importLayout = (exportedLayout) => {
  if (!exportedLayout || !exportedLayout.layout) {
    throw new Error('Format de layout invalide');
  }

  return exportedLayout.layout.map((item) => ({
    i: item.id,
    x: item.x || 0,
    y: item.y || 0,
    w: item.w || 4,
    h: item.h || 4,
    static: item.static || false,
  }));
};

/**
 * Calcule la position optimale pour un nouveau bloc
 * @param {array} existingLayout - Layout existant
 * @param {object} newBlock - Nouveau bloc à placer
 * @param {string} breakpoint - Breakpoint cible
 * @returns {object} Position optimale {x, y}
 */
export const findOptimalPosition = (
  existingLayout,
  newBlock,
  breakpoint = 'lg'
) => {
  const maxCols = DEFAULT_GRID_CONFIG.cols[breakpoint];
  const blockSize = getDefaultBlockSize(newBlock);

  // Tenter de placer le bloc ligne par ligne
  for (let y = 0; y < 100; y++) {
    // Limiter à 100 rangées pour éviter les boucles infinies
    for (let x = 0; x <= maxCols - blockSize.w; x++) {
      const testPosition = { x, y, w: blockSize.w, h: blockSize.h, i: 'test' };

      // Vérifier s'il y a collision avec les blocs existants
      const hasCollision = existingLayout.some((item) => {
        return !(
          testPosition.x + testPosition.w <= item.x ||
          item.x + item.w <= testPosition.x ||
          testPosition.y + testPosition.h <= item.y ||
          item.y + item.h <= testPosition.y
        );
      });

      if (!hasCollision) {
        return { x, y };
      }
    }
  }

  // Si aucune position libre trouvée, placer en bas
  const maxY = Math.max(...existingLayout.map((item) => item.y + item.h), 0);
  return { x: 0, y: maxY };
};

/**
 * Redimensionne automatiquement le layout pour un nouveau breakpoint
 * @param {array} layout - Layout actuel
 * @param {string} fromBreakpoint - Breakpoint source
 * @param {string} toBreakpoint - Breakpoint cible
 * @returns {array} Layout redimensionné
 */
export const resizeLayoutForBreakpoint = (
  layout,
  fromBreakpoint,
  toBreakpoint
) => {
  const fromCols = DEFAULT_GRID_CONFIG.cols[fromBreakpoint];
  const toCols = DEFAULT_GRID_CONFIG.cols[toBreakpoint];
  const ratio = toCols / fromCols;

  return layout.map((item) => ({
    ...item,
    x: Math.floor(item.x * ratio),
    w: Math.max(1, Math.floor(item.w * ratio)),
    // Garder la hauteur et position Y inchangées
  }));
};

/**
 * Compacte le layout en supprimant les espaces vides
 * @param {array} layout - Layout à compacter
 * @param {string} direction - Direction de compactage ('vertical', 'horizontal')
 * @returns {array} Layout compacté
 */
export const compactLayout = (layout, direction = 'vertical') => {
  if (direction === 'vertical') {
    // Trier par Y puis par X
    const sorted = [...layout].sort((a, b) => a.y - b.y || a.x - b.x);

    // Déplacer chaque bloc vers le haut autant que possible
    sorted.forEach((item) => {
      let newY = 0;

      // Trouver la position Y la plus haute possible
      while (newY < item.y) {
        const testItem = { ...item, y: newY };
        const hasCollision = sorted.some((other) => {
          if (other.i === item.i) return false;
          return !(
            testItem.x + testItem.w <= other.x ||
            other.x + other.w <= testItem.x ||
            testItem.y + testItem.h <= other.y ||
            other.y + other.h <= testItem.y
          );
        });

        if (hasCollision) break;
        newY++;
      }

      item.y = Math.max(0, newY - 1);
    });

    return sorted;
  }

  return layout;
};

/**
 * Génère un ID unique pour un nouveau bloc
 * @param {array} existingBlocks - Blocs existants
 * @param {string} prefix - Préfixe pour l'ID
 * @returns {string} ID unique
 */
export const generateUniqueBlockId = (
  existingBlocks = [],
  prefix = 'block'
) => {
  const existingIds = existingBlocks.map((block) => block.id);
  let counter = 0;
  let newId;

  do {
    newId = `${prefix}_${counter}`;
    counter++;
  } while (existingIds.includes(newId));

  return newId;
};

export default {
  DEFAULT_GRID_CONFIG,
  DEFAULT_BLOCK_SIZES,
  MIN_BLOCK_SIZES,
  ensureBlockIds,
  generateLayout,
  validateLayout,
  optimizeLayout,
  exportLayout,
  importLayout,
  findOptimalPosition,
  resizeLayoutForBreakpoint,
  compactLayout,
  generateUniqueBlockId,
};
